// Ordered lists. The upstream tokenizer gets the source only up to where the
// list surely ends, instead of the whole rest.
//
// Upstream splits `src` by line on every call, and marked calls every
// extension's `tokenize` with the whole rest at every block boundary - even
// when no list starts there - so one parse cost "blocks x document length".
// How lists are read is unchanged.

import type { MarkdownToken } from "@tiptap/core";
import { OrderedList } from "@tiptap/extension-list";
import { firstLine } from "./lines";

/** Upstream's `ORDERED_LIST_ITEM_REGEX` (an item line). */
const ITEM_RE = /^(\s*)(\d+|[ivxlcdmIVXLCDM]+|[a-zA-Z]{1,2})([.)])\s+(.*)$/;

type Tokenize = (
  src: string,
  tokens: MarkdownToken[],
  helpers: unknown,
) => MarkdownToken | undefined;

/**
 * Where upstream surely stops reading the ordered list at `src`: the line break
 * before the first unindented non-item line after a blank line (upstream leaves
 * an item there).
 *
 * The position of the line break, not of the line: cutting at the line start
 * makes upstream read the trailing empty line as a blank line of the item and
 * consume one more character.
 */
export function orderedListBound(src: string): number {
  let prevBlank = false;
  let lineStart = src.indexOf("\n") + 1;
  while (lineStart > 0 && lineStart < src.length) {
    const nl = src.indexOf("\n", lineStart);
    const line = src.slice(lineStart, nl === -1 ? src.length : nl);
    const blank = line.trim() === "";
    if (prevBlank && !blank && !/^\s/.test(line) && !ITEM_RE.test(line)) {
      return lineStart - 1;
    }
    prevBlank = blank;
    lineStart = nl + 1;
  }
  return src.length;
}

const upstream = OrderedList.config.markdownTokenizer;

export const TrameriaOrderedList = OrderedList.extend({
  markdownTokenizer: upstream && {
    ...upstream,
    tokenize(src: string, tokens: MarkdownToken[], helpers) {
      // Upstream reads nothing unless the first line is an item; return before splitting.
      if (!ITEM_RE.test(firstLine(src))) return undefined;
      return (upstream.tokenize as Tokenize)(
        src.slice(0, orderedListBound(src)),
        tokens,
        helpers,
      );
    },
  },
});
